import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card, CardContent, CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Snackbar,
  Typography
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { useCallback, useEffect, useState } from "react";

// İkonlar
import {
  AddToHomeScreen,
  BatteryAlert,
  BatteryFull,
  BugReport,
  Delete,
  Memory,
  Refresh,
  SettingsInputComponent,
  Share,
  Storage,
  Wifi
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 800,
    margin: "12px auto",
    borderRadius: 20,
    boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
    backgroundColor: "#fff",
    [theme.breakpoints.down('xs')]: {
      margin: "8px",
      borderRadius: 12,
    },
  },
  header: {
    background: "linear-gradient(135deg, #424242 0%, #424242 100%)",
    color: "#FF9800",
    padding: theme.spacing(2),
    "& .MuiCardHeader-subheader": { color: "#ff9800", fontSize: '0.8rem' },
  },
  sectionHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    fontWeight: 700,
    color: "#424242",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: '1rem'
  },
  infoBox: {
    backgroundColor: "#fcfcfc",
    padding: theme.spacing(1.5),
    borderRadius: 12,
    height: "100%",
    minHeight: "80px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: "1px solid #eeeeee",
    transition: "transform 0.2s",
    "&:active": { transform: "scale(0.98)" }
  },
  logBox: {
    backgroundColor: "#2d2d2d",
    color: "#FF9800",
    padding: theme.spacing(1.5),
    borderRadius: 10,
    fontFamily: "'Courier New', monospace",
    fontSize: "0.7rem",
    maxHeight: "120px",
    overflowY: "auto",
    border: "2px solid #424242"
  },
  actionButton: {
    borderRadius: 10,
    padding: theme.spacing(1.2),
    fontWeight: 600,
    textTransform: "none"
  }
}));

const OrangeProgress = withStyles({
  root: { height: 8, borderRadius: 4 },
  colorPrimary: { backgroundColor: "#eeeeee" },
  bar: { borderRadius: 4, backgroundColor: '#FF9800' },
})(LinearProgress);

export default function PwaFinalDiagnostics() {
  const classes = useStyles();

  // --- STATE ---
  const [pwaInfo, setPwaInfo] = useState({ isInstalled: false, swActive: false });
  const [network, setNetwork] = useState({ rtt: '?', downlink: '?', type: '?' });
  const [storageStats, setStorageStats] = useState({ used: 0, total: 1 });
  const [battery, setBattery] = useState({ level: 0, charging: false, supported: false });
  const [hardware, setHardware] = useState({ cores: '?', memory: '?' });
  const [logs, setLogs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", type: "info" });
  const [isIOS, setIsIOS] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  const addLog = useCallback((msg) => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [{ time, msg }, ...prev].slice(0, 8));
  }, []);

  useEffect(() => {
    // Mobil Cihaz & PWA Kontrolü
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    
    const checkPwa = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setPwaInfo({
        isInstalled: !!isStandalone,
        swActive: !!(navigator.serviceWorker && navigator.serviceWorker.controller)
      });
    };
    checkPwa();

    // Ağ Testi
    if (navigator.connection) {
      const updateNet = () => setNetwork({
        rtt: navigator.connection.rtt + "ms",
        downlink: navigator.connection.downlink + "Mbps",
        type: navigator.connection.effectiveType
      });
      navigator.connection.addEventListener('change', updateNet);
      updateNet();
    }

    // Donanım
    setHardware({
      cores: navigator.hardwareConcurrency || '?',
      memory: navigator.deviceMemory || '?'
    });

    // Depolama
    if (navigator.storage?.estimate) {
      navigator.storage.estimate().then(({ usage, quota }) => {
        setStorageStats({ 
          used: (usage / 1024 / 1024).toFixed(1), 
          total: (quota / 1024 / 1024 / 1024).toFixed(1) 
        });
      });
    }

    // Pil
    if (navigator.getBattery) {
      navigator.getBattery().then(bat => {
        const up = () => setBattery({ level: bat.level, charging: bat.charging, supported: true });
        up();
        bat.onlevelchange = up;
        bat.onchargingchange = up;
      });
    }

    addLog("Sistem kontrolü tamamlandı.");
  }, [addLog]);

  const handleAction = async (type) => {
    try {
      if (type === 'update') {
        setSnackbar({ open: true, msg: "Yeni versiyon çekiliyor...", type: "info" });
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map(n => caches.delete(n)));
        }
        if (navigator.serviceWorker) {
          const regs = await navigator.serviceWorker.getRegistrations();
          for (let r of regs) await r.update();
        }
        addLog("Önbellek temizlendi ve güncellendi.");
        setTimeout(() => window.location.reload(), 1000);
      } 
      else if (type === 'reset') {
        if (!window.confirm("Tüm uygulama verileri sıfırlanacak. Devam edilsin mi?")) return;
        localStorage.clear();
        sessionStorage.clear();
        addLog("Kullanıcı verileri temizlendi.");
        setSnackbar({ open: true, msg: "Veriler sıfırlandı!", type: "success" });
        setTimeout(() => window.location.replace("/"), 1000);
      }
    } catch (e) {
      addLog("Hata: " + e.message);
    }
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        className={classes.header}
        avatar={<Avatar style={{ backgroundColor: '#FF9800', color: '#424242' }}><SettingsInputComponent /></Avatar>}
        title={<Typography variant="subtitle1" style={{ fontWeight: 800,color: '#FF9800' }}>SİSTEM TANI PANELİ</Typography>}
        subheader={pwaInfo.isInstalled ? "UYGULAMA MODU AKTİF" : "TARAYICI ÜZERİNDEN ERİŞİM"}
      />
      <CardContent style={{ padding: '16px' }}>
        
        {/* Ağ Bilgileri */}
        <Typography variant="body2" className={classes.sectionHeader}><Wifi fontSize="small" style={{color: '#FF9800'}}/> BAĞLANTI & HIZ</Typography>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <div className={classes.infoBox}>
              <Typography variant="caption" color="textSecondary">Tip</Typography>
              <Typography variant="body2" style={{fontWeight:700}}>{network.type.toUpperCase()}</Typography>
            </div>
          </Grid>
          <Grid item xs={4}>
            <div className={classes.infoBox}>
              <Typography variant="caption" color="textSecondary">Gecikme</Typography>
              <Typography variant="body2" style={{fontWeight:700}}>{network.rtt}</Typography>
            </div>
          </Grid>
          <Grid item xs={4}>
            <div className={classes.infoBox}>
              <Typography variant="caption" color="textSecondary">Hız</Typography>
              <Typography variant="body2" style={{fontWeight:700}}>{network.downlink}</Typography>
            </div>
          </Grid>
        </Grid>

        {/* Donanım ve Pil */}
        <Typography variant="body2" className={classes.sectionHeader}><Memory fontSize="small" style={{color: '#FF9800'}}/> DONANIM DURUMU</Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <div className={classes.infoBox}>
              <Box display="flex" alignItems="center" gap={1}>
                {battery.charging ? <BatteryFull style={{color: '#4caf50', fontSize: 18}} /> : <BatteryAlert style={{fontSize: 18}}/>}
                <Typography variant="body2" style={{fontWeight:700}}>Pil: %{Math.round(battery.level * 100)}</Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">{battery.charging ? 'Şarj Oluyor' : 'Deşarj'}</Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={classes.infoBox}>
              <Typography variant="body2" style={{fontWeight:700}}>RAM: {hardware.memory} GB</Typography>
              <Typography variant="caption" color="textSecondary">CPU: {hardware.cores} Çekirdek</Typography>
            </div>
          </Grid>
        </Grid>

        <Divider style={{ margin: "16px 0" }} />

        {/* Depolama */}
        <Typography variant="body2" className={classes.sectionHeader}><Storage fontSize="small" style={{color: '#FF9800'}}/> DEPOLAMA (CACHE)</Typography>
        <Box px={1} mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="textSecondary">Kullanılan: {storageStats.used} MB</Typography>
            <Typography variant="caption" color="textSecondary">Limit: {storageStats.total} GB</Typography>
          </Box>
          <OrangeProgress variant="determinate" value={Math.min((storageStats.used / 500) * 100, 100)} />
        </Box>

        {/* Aksiyon Butonları */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button 
              className={classes.actionButton} 
              fullWidth 
              variant="contained" 
              color="secondary"
              startIcon={<Refresh />} 
              onClick={() => handleAction('update')}
            >
              Güncelle
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button 
              className={classes.actionButton} 
              fullWidth 
              variant="contained" 
              color="secondary"
              startIcon={<Delete />} 
              onClick={() => handleAction('reset')}
            >
              Sıfırla
            </Button>
          </Grid>
        </Grid>

        {/* Sistem Günlüğü */}
        <Typography variant="body2" className={classes.sectionHeader}><BugReport fontSize="small" style={{color: '#FF9800'}}/> SİSTEM GÜNLÜĞÜ</Typography>
        <div className={classes.logBox}>
          {logs.map((l, i) => (
            <div key={i} style={{marginBottom: 2}}>• [{l.time}] {l.msg}</div>
          ))}
        </div>

      </CardContent>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" style={{backgroundColor: snackbar.type === 'success' ? '#424242' : '#FF9800'}}>
          {snackbar.msg}
        </Alert>
      </Snackbar>

      {/* iOS Yükleme Kılavuzu */}
      <Dialog open={showInstallPrompt && isIOS && !pwaInfo.isInstalled} onClose={() => setShowInstallPrompt(false)}>
        <DialogTitle style={{color: '#424242'}}>Uygulamayı Yükle</DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <Typography variant="body2">iOS cihazlarda tam ekran moduna geçmek için:</Typography>
            <Box my={2} p={2} bgcolor="#fff3e0" borderRadius={8} border="1px dashed #FF9800">
              <Typography variant="body2">1. Alt menüden <strong>Paylaş</strong> <Share fontSize="small"/> tuşuna basın.</Typography>
              <Typography variant="body2" style={{marginTop: 8}}>2. <strong>Ana Ekrana Ekle</strong> <AddToHomeScreen fontSize="small"/> seçeneğini seçin.</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallPrompt(false)} style={{color: '#FF9800'}}>Anladım</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}